import { useMutation } from '@apollo/client';
import { Grid } from '@mui/material';
import React, { useContext, useMemo, useState } from 'react';
import { BinContext } from '../../../../../../context/BinContext';
import { BinStatusContext } from '../../../../../../context/BinStatusContext';
import { CompanyContext } from '../../../../../../context/CompanyContext';
import { ContainerContext } from '../../../../../../context/ContainerContext';
import { CustomFieldContext } from '../../../../../../context/CustomFieldContext';
import { LotContext } from '../../../../../../context/LotContext';
import { ProductContext } from '../../../../../../context/ProductContext';
import { HandlingUnitContext } from '../../../../../../context/HandlingUnitContext';
import { ProductTransactionContext } from '../../../../../../context/ProductTransactionContext';
import { StockLocationContext } from '../../../../../../context/StockLocationContext';
import { StockLocationRoleAssignmentContext } from '../../../../../../context/StockLocationRoleAssignmentContext';
import {
  CreateAndProcessProductTransactionResponse,
  CreateAndProcessProductTransactionVariables,
  ProductTransactionMutations,
} from '../../../../../../graphql/productTransaction.graphql';
import { CustomFieldEntityType } from '../../../../../../types/custom_field';
import { Product } from '../../../../../../types/product';
import {
  ProductTransaction,
  ProductTransactionType,
  ProductTransactionStatus,
  ProductTransactionProcessType,
  productTransactionTypeToCustomFieldEntitySubType,
} from '../../../../../../types/productTransaction';
import { TranslationKey } from '../../../../../../types/translation/TranslationKey';
import { t, translate } from '../../../../../../types/translation/Translator';
import { productTransactionReasonForFailureToString } from '../../../../../../util/error.util';
import { ActionModalPane } from '../../../../../components/Modal';
import { Button, ButtonVariant, CancelButton } from '../../../../../components/Button';
import { ProductMasterData } from '../../../../../../types/product_master_data/productMasterData';
import { useToast } from '../../../../../../providers/ToastProvider';
import { ToastType } from '../../../../../components/Toast';
import ProcessOrderTransactionDefaultBody, {
  ProcessTransactionProps,
} from '../../../../Operation/Order/Modals/Panes/Views/ProcessOrderTransactionDefaultBody';
import { determineProduct } from '../../../../../../util/containers.util';
import { Checkbox } from '../../../../../components/Checkbox';
import { Company } from '../../../../../../types/company';
import { UserContext } from '../../../../../../context/UserContext';
import GenerateBarcodeButton from '../../../../../components/Button/templates/GenerateBarcodeButton';
import { BarcodeExportEntityType } from '../../../../../../types/barcode_export_configuration/barcodeExportConfiguration';
import { UnitOfMeasure } from '../../../../../../types/uom';
import { CreateOrderFromStockModalContext } from '../../../../../../providers/CreateOrderFromStockModalProvider';

interface QuickActionTransactionViewProps {
  setOpen: (open: boolean) => void;
  pmd: ProductMasterData;
  productId?: string;

  props: ProcessTransactionProps;
}

export default function QuickActionTransactionView({
  setOpen,
  pmd,
  productId,
  props,
}: QuickActionTransactionViewProps) {
  const { showToast } = useToast();

  const { openCreateOrderFromStockModal } = useContext(CreateOrderFromStockModalContext);

  const { currentCompany } = useContext(CompanyContext);
  const { productTransactions, setProductTransactions } = useContext(ProductTransactionContext);
  const { products } = useContext(ProductContext);
  const { handlingUnits } = useContext(HandlingUnitContext);
  const { stockLocations } = useContext(StockLocationContext);
  const { containers } = useContext(ContainerContext);
  const { bins } = useContext(BinContext);
  const { lots } = useContext(LotContext);
  const { binStatuses } = useContext(BinStatusContext);
  const { customFields } = useContext(CustomFieldContext);
  const { currentUser } = useContext(UserContext);
  const { hasStockLocationRole } = useContext(StockLocationRoleAssignmentContext);

  const emptyProduct = {
    companyId: currentCompany.id,
    productMasterDataId: pmd.id,
    stockLocationId: [...stockLocations.values()][0].id,
    quantity: UnitOfMeasure.toQuantity(
      pmd.unitOfMeasure,
      products.get(pmd.id)?.get(productId || '')?.unitQuantity || '0',
    ),
  };

  const [product, setProduct] = useState<Product>(
    new Product(products.get(pmd.id)?.get(productId || '') || emptyProduct),
  );

  const toContainer = props.toContainerId ? containers.get(props.toContainerId) : undefined;

  const [isMove, setIsMove] = useState<boolean>(false);

  function getInitialTransaction() {
    if (productId) {
      return {
        pmdId: product.productMasterDataId,
        fromStockLocationId: product.stockLocationId,
        fromBinId: product.binId,
        fromContainerId: product.containerId,
        lotId: product.lotId,
        serialNbr: product.serial,
        lpn: product.lpn,
        fromCustomFields: Product.fromIdentifier(customFields, product.identifier),
        toCustomFields: Product.fromIdentifier(customFields, product.identifier),
        kit: { fromKitId: product?.kitId, toKitId: product?.kitId },
        quantity:
          pmd.serialManaged || (pmd.lpnManaged && !pmd.lotManaged)
            ? 1
            : parseFloat(UnitOfMeasure.toQuantity(pmd.unitOfMeasure, product.unitQuantity)),
      };
    }

    const specifiers = {
      pmdId: pmd.id,
      quantity: 1,
    };

    if (props.toContainerId) {
      return {
        ...specifiers,
        toContainerId: props.toContainerId,
        toStockLocationId: toContainer?.stockLocationId,
        toBinId: toContainer?.binId,
      };
    }

    return specifiers;
  }

  const [transaction, setTransaction] = useState<ProductTransaction>(
    new ProductTransaction({
      type: !productId ? ProductTransactionType.inbound : ProductTransactionType.outbound,
      status: ProductTransactionStatus.created,
      companyId: currentCompany.id,
      product: getInitialTransaction(),
    }),
  );

  const relevantCustomFields = useMemo(() => {
    return [...customFields.values()].filter(
      cf =>
        (!cf.generated &&
          !cf.hidden &&
          cf.entityType === CustomFieldEntityType.productTransaction &&
          cf.entitySubtype === productTransactionTypeToCustomFieldEntitySubType(transaction.type)) ||
        cf.entityType === CustomFieldEntityType.product,
    );
  }, [customFields]);

  const [process, { loading }] = useMutation<
    CreateAndProcessProductTransactionResponse,
    CreateAndProcessProductTransactionVariables
  >(ProductTransactionMutations.createAndProcess, {
    onCompleted: res => {
      const transaction = res.createAndProcessTransaction[0];
      productTransactions.set(transaction.id, new ProductTransaction(transaction));
      setProductTransactions(new Map(productTransactions));
      showToast({
        type: ToastType.success,
        title: t(TranslationKey.productTransactionProcessedSuccessfullyToastTitle).singular.upper,
      });
      setOpen(false);
    },
    onError: err =>
      showToast({ type: ToastType.error, title: productTransactionReasonForFailureToString(err.message) }),
  });

  const handleProcess = async () => {
    transaction.product.quantity = transaction.product.processedQuantity || 0;
    await process({
      variables: {
        productTransactions: [transaction.forUpdate()],
      },
    });
  };

  const scanRequired = Company.hasScanRequired(currentCompany, currentUser || undefined);

  const isDisabled = () => {
    if (scanRequired) return true;

    if (
      !transaction.product.processedQuantity ||
      transaction.product.processedQuantity === 0 ||
      isNaN(transaction.product.processedQuantity)
    ) {
      return true;
    }

    if (transaction.processType === ProductTransactionProcessType.asset) return false;

    if (transaction.type === ProductTransactionType.inbound) {
      for (const customField of relevantCustomFields.filter(cf => cf.entityType === CustomFieldEntityType.product)) {
        if (!customField.mandatory) continue;
      }
    }

    if (pmd?.serialManaged && !transaction.product.serialNbr) return true;
    if (pmd?.lpnManaged && !transaction.product.lpn) return true;
    if (pmd?.lotManaged && !transaction.product.lotId) return true;
    if (
      (transaction.type === ProductTransactionType.inbound || transaction.type === ProductTransactionType.move) &&
      !transaction.product.toBinId
    ) {
      return true;
    }

    if (
      (transaction.type === ProductTransactionType.outbound || transaction.type === ProductTransactionType.move) &&
      !transaction.product.fromBinId
    ) {
      return true;
    }

    if (!new Set([ProductTransactionStatus.created, ProductTransactionStatus.picked]).has(transaction.status)) {
      return true;
    }

    if (transaction.product.containerId) return false;

    if (
      transaction.type === ProductTransactionType.outbound ||
      (transaction.type === ProductTransactionType.move && transaction.status !== ProductTransactionStatus.picked)
    ) {
      const existing = determineProduct(transaction, products, customFields, handlingUnits);
      if (!existing) return true;
    }

    return false;
  };

  const handleClose = () => {
    setProduct(new Product(emptyProduct));
    setOpen(false);
  };

  const processText = () => {
    let process = t(TranslationKey.process).singular.upper;
    if (transaction.type === ProductTransactionType.inbound) {
      process += ` (${t(TranslationKey.inbound).singular.upper})`;
    } else if (transaction.type === ProductTransactionType.outbound) {
      process += ` (${t(TranslationKey.outbound).singular.upper})`;
    } else if (transaction.type === ProductTransactionType.move) {
      process += ` (${t(TranslationKey.move).singular.upper})`;
    }
    return process;
  };

  const footer = (
    <div className='flex justify-between gap-2'>
      <div className='flex gap-2'>
        {productId ? (
          <>
            <GenerateBarcodeButton entityIds={[productId]} entityType={BarcodeExportEntityType.product} />
            <Button
              variant={ButtonVariant.link}
              text={translate({ key: TranslationKey.stockTransfer })}
              onClick={() => {
                setOpen(false);
                openCreateOrderFromStockModal(product);
              }}
            />
          </>
        ) : null}
      </div>
      <div className='flex gap-2'>
        <CancelButton disabled={loading} onClick={handleClose} />
        <Button disabled={isDisabled()} loading={loading} onClick={handleProcess} text={processText()} />
      </div>
    </div>
  );

  return (
    <ActionModalPane footer={footer}>
      <Grid container height={'100%'} rowSpacing={1} alignContent={'space-between'}>
        <Grid item xs={12}>
          {scanRequired && (
            <div className='mb-2 border border-warning-300 bg-warning-100 px-2 py-2 text-center text-warning-400'>
              <p className='text-sm'>{t(TranslationKey.scanRequiredWarning).singular.label}</p>
            </div>
          )}
          <Grid container columnSpacing={1} rowSpacing={1}>
            {productId ? (
              <Grid item xs={12}>
                <Checkbox
                  value={isMove}
                  disabled={scanRequired}
                  label={t(TranslationKey.move).singular.label}
                  onChange={() => {
                    setIsMove(!isMove);
                    setTransaction(
                      transaction.withType(!isMove ? ProductTransactionType.move : ProductTransactionType.outbound),
                    );
                  }}
                />
              </Grid>
            ) : null}
            <Grid item xs={12}>
              <ProcessOrderTransactionDefaultBody
                transaction={transaction}
                setTransaction={setTransaction}
                productMasterData={pmd}
                product={productId ? product : undefined}
                disabled={scanRequired}
                props={props}
              />
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </ActionModalPane>
  );
}
